import { authClient, getSession } from "@/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

const verifyLoginSearchSchema = z.object({
  redirectTo: z.string().optional(),
  emailAddress: z.string().email().optional(),
});

export const Route = createFileRoute("/(auth)/login")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.fetchQuery({
      queryKey: ["session"],
      queryFn: () => getSession(),
    });

    if (session) {
      throw redirect({ to: "/" });
    }
  },
  component: LoginComponent,
  validateSearch: zodValidator(verifyLoginSearchSchema),
});

function LoginComponent() {
  const { redirectTo, emailAddress } = Route.useSearch();
  const [email, setEmail] = useState(emailAddress ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleRequestOtp = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!email) {
      setError("El email es obligatorio");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const { error: otpError } = await authClient.emailOtp.sendVerificationOtp(
        { email, type: "sign-in" },
      );

      if (otpError) {
        throw new Error(otpError.message);
      }

      navigate({
        to: "/code",
        search: { email, redirectTo },
      });
    } catch (err) {
      console.error("Error requesting OTP:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Error al enviar el código de verificación. Inténtalo de nuevo.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-100 dark:bg-slate-900 flex flex-col">
      {/* Mobile fullscreen layout */}
      <div className="flex-1 flex items-center justify-center md:p-8">
        <div className="w-full max-w-md">
          <Card className="h-screen md:h-auto md:shadow-xl border-0 md:border bg-white dark:bg-slate-800 md:bg-white/95 md:dark:bg-slate-800/95 md:backdrop-blur md:rounded-lg rounded-none">
            <CardHeader className="text-center space-y-6 pb-4 px-6 md:px-8 pt-8 md:pt-6">
              {/* Logo */}
              <div className="flex justify-center">
                <img src="/logo.svg" alt="HSE Chain" className="h-12 w-auto" />
              </div>

              {/* Title and Description */}
              <div className="space-y-2">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  👋 Bienvenido
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-400">
                  Ingresa tu correo para acceder a la plataforma.
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 px-6 md:px-8">
              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg border border-red-200 dark:border-red-800">
                  {error}
                </div>
              )}

              <form onSubmit={handleRequestOtp} className="space-y-6">
                <div className="space-y-2">
                  <Input
                    id="email"
                    type="email"
                    placeholder="nombre@ejemplo.com"
                    required
                    className="h-12 text-base border-gray-300 dark:border-gray-600 focus:border-primary focus:ring-primary"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    value={email}
                    disabled={loading}
                  />
                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  className="w-full"
                  disabled={loading || !email}
                >
                  {loading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    "Acceder"
                  )}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="pt-6 px-6 md:px-8">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center w-full">
                Al continuar, aceptas nuestros{" "}
                <a
                  href="https://hsechain.com/service-terms/"
                  className="text-primary hover:text-primary/80 underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  términos de servicio
                </a>
                .
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
